// File Location: src/services/userService.js

import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  addDoc,
  getDoc,
  limit,
  startAfter,
  writeBatch,
  setDoc
} from "firebase/firestore";
import { auth, db, secondaryAuth, functions } from "./firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
export const createUser = async (userData) => {
  try {
    console.log('Creating user with data:', userData);

    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      userData.email,
      userData.password
    );

    console.log('Firebase Auth user created:', userCredential.user.uid);

    // Prepare user data for Firestore
    const firestoreUserData = {
      uid: userCredential.user.uid,
      email: userData.email,
      role: userData.role,
      // New multi-role support: store roles array while keeping existing role string for compatibility
      roles: Array.isArray(userData.roles) && userData.roles.length > 0
        ? userData.roles
        : (userData.role ? [userData.role] : []),
      isActive: userData.isActive !== undefined ? userData.isActive : true,
      isBlocked: false,
      createdAt: new Date(),
      createdBy: userData.createdBy,
      updatedAt: new Date(),
      profile: {
        name: userData.name,
        phone: userData.phone || '',
        email: userData.email,
        designation: userData.designation || '',
        department: userData.department || '',
        employeeId: userData.employeeId || '',
      },
      // Role-specific fields can be added here based on role
      ...(userData.role === 'SystemAdmin' && {
        permissions: [
          'approve_hospitals',
          'approve_lead_generators',
          'review_patient_applications',
          'access_system_settings',
          'generate_reports'
        ]
      }),
      ...(userData.role === 'Doctor' && {
        permissions: [
          'verify_medical_documents',
          'add_medical_comments',
          'generate_kyp_letter',
          'chat_with_hospitals'
        ]
      }),
      ...(userData.role === 'FinancialTeam' && {
        permissions: [
          'process_reimbursements',
          'generate_reports',
          'track_payments'
        ]
      }),
      ...(userData.role === 'PolicyWriter' && {
        permissions: [
          'verify_policy_details',
          'add_policy_comments',
          'approve_policies'
        ]
      }),
    };

    // Save user data to Firestore using the UID as document ID
    await setDoc(doc(db, "users", userCredential.user.uid), firestoreUserData);

    console.log('User data saved to Firestore');

    // Log the activity
    await addDoc(collection(db, "userActivities"), {
      action: "user_created",
      targetUserId: userCredential.user.uid,
      performedBy: userData.createdBy,
      timestamp: new Date(),
      details: {
        userEmail: userData.email,
        userRole: userData.role,
        actionType: "create"
      }
    });

  const response = {
      success: true,
      user: {
        uid: userCredential.user.uid,
        ...firestoreUserData,
      },
      message: "User created successfully",
  };
  // Sign out secondary auth so admin session remains intact and no lingering sign-in on secondary app
  try { await signOut(secondaryAuth); } catch (e) { /* noop */ }
  return response;
  } catch (error) {
    console.error("Error creating user:", error);
    
    // Handle specific Firebase Auth errors
    let errorMessage = "Failed to create user";
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = "Email address is already in use";
    } else if (error.code === 'auth/weak-password') {
      errorMessage = "Password is too weak";
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = "Invalid email address";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};
// Get all users from the users collection
export const getAllUsers = async (pageSize = 1000, lastDoc = null) => {
  try {
    let usersQuery = query(
      collection(db, "users"),
      orderBy("createdAt", "desc")
    );

    if (pageSize) {
      usersQuery = query(usersQuery, limit(pageSize));
    }

    if (lastDoc) {
      usersQuery = query(usersQuery, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(usersQuery);
    const users = [];
    let lastDocument = null;

    querySnapshot.forEach((doc) => {
      users.push({
        uid: doc.id,
        ...doc.data(),
      });
      lastDocument = doc;
    });

    return {
      success: true,
      users,
      lastDocument,
      hasMore: querySnapshot.size === pageSize,
    };
  } catch (error) {
    console.error("Error fetching users:", error);
    return {
      success: false,
      error: error.message,
      users: [],
    };
  }
};

// Get users by role
export const getUsersByRole = async (role) => {
  try {
    // Support both legacy role string and new roles[] array using two queries, then merge client-side
    const baseRef = collection(db, "users");

  const legacyQuery = query(baseRef, where("role", "==", role));
  const multiRoleQuery = query(baseRef, where("roles", "array-contains", role));

    const [legacySnap, multiSnap] = await Promise.all([
      getDocs(legacyQuery),
      getDocs(multiRoleQuery),
    ]);

    const usersMap = new Map();

    legacySnap.forEach((docSnap) => {
      usersMap.set(docSnap.id, { uid: docSnap.id, ...docSnap.data() });
    });

    multiSnap.forEach((docSnap) => {
      usersMap.set(docSnap.id, { uid: docSnap.id, ...docSnap.data() });
    });

    // Convert to array and sort by createdAt desc (in case merge changed order)
    const users = Array.from(usersMap.values()).sort((a, b) => {
      const aTs = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const bTs = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return bTs - aTs;
    });

    return {
      success: true,
      users,
    };
  } catch (error) {
    console.error("Error fetching users by role:", error);
    return {
      success: false,
      error: error.message,
      users: [],
    };
  }
};

// Get single user by UID
export const getUserByUid = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: "User not found",
      };
    }

    return {
      success: true,
      user: {
        uid: userDoc.id,
        ...userDoc.data(),
      },
    };
  } catch (error) {
    console.error("Error fetching user:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Block user account
export const blockUser = async (userUid, blockedBy, reason) => {
  try {
    const userRef = doc(db, "users", userUid);
    
    // Update user document
    await updateDoc(userRef, {
      isBlocked: true,
      blockedBy: blockedBy,
      blockedAt: new Date(),
      blockReason: reason,
      updatedAt: new Date(),
    });

    // Log the activity
    await addDoc(collection(db, "userActivities"), {
      action: "user_blocked",
      targetUserId: userUid,
      performedBy: blockedBy,
      reason: reason,
      timestamp: new Date(),
      details: {
        userUid: userUid,
        actionType: "block"
      }
    });

    return {
      success: true,
      message: "User blocked successfully",
    };
  } catch (error) {
    console.error("Error blocking user:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Unblock user account
export const unblockUser = async (userUid, unblockedBy) => {
  try {
    const userRef = doc(db, "users", userUid);
    
    // Update user document
    await updateDoc(userRef, {
      isBlocked: false,
      unblockedBy: unblockedBy,
      unblockedAt: new Date(),
      blockReason: null, // Clear the block reason
      updatedAt: new Date(),
    });

    // Log the activity
    await addDoc(collection(db, "userActivities"), {
      action: "user_unblocked",
      targetUserId: userUid,
      performedBy: unblockedBy,
      timestamp: new Date(),
      details: {
        userUid: userUid,
        actionType: "unblock"
      }
    });

    return {
      success: true,
      message: "User unblocked successfully",
    };
  } catch (error) {
    console.error("Error unblocking user:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Update user status (activate/deactivate)
export const updateUserStatus = async (userUid, isActive, updatedBy, reason = null) => {
  try {
    const userRef = doc(db, "users", userUid);
    
    const updateData = {
      isActive: isActive,
      updatedBy: updatedBy,
      updatedAt: new Date(),
    };

    if (!isActive && reason) {
      updateData.deactivationReason = reason;
      updateData.deactivatedAt = new Date();
    } else if (isActive) {
      updateData.reactivatedAt = new Date();
      updateData.deactivationReason = null;
    }

    await updateDoc(userRef, updateData);

    // Log the activity
    await addDoc(collection(db, "userActivities"), {
      action: isActive ? "user_activated" : "user_deactivated",
      targetUserId: userUid,
      performedBy: updatedBy,
      reason: reason,
      timestamp: new Date(),
      details: {
        userUid: userUid,
        newStatus: isActive,
        actionType: isActive ? "activate" : "deactivate"
      }
    });

    return {
      success: true,
      message: `User ${isActive ? 'activated' : 'deactivated'} successfully`,
    };
  } catch (error) {
    console.error(`Error ${isActive ? 'activating' : 'deactivating'} user:`, error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get user statistics
export const getUserStats = async () => {
  try {
    const usersRef = collection(db, "users");

    // Get all users
    const allUsersQuery = await getDocs(usersRef);
    
    // Get active users
    const activeUsersQuery = await getDocs(
      query(usersRef, where("isActive", "==", true), where("isBlocked", "==", false))
    );
    
    // Get blocked users
    const blockedUsersQuery = await getDocs(
      query(usersRef, where("isBlocked", "==", true))
    );

    // Get users by role
    const roleStats = {};
    const roles = ["SystemAdmin", "LeadGenerator", "Hospital", "Doctor", "FinancialTeam", "PolicyWriter"];
    
    for (const role of roles) {
      // Count unique users who either have legacy role or roles[] contains role
      const [legacySnap, multiSnap] = await Promise.all([
        getDocs(query(usersRef, where("role", "==", role))),
        getDocs(query(usersRef, where("roles", "array-contains", role))),
      ]);
      const idSet = new Set();
      legacySnap.forEach(d => idSet.add(d.id));
      multiSnap.forEach(d => idSet.add(d.id));
      roleStats[role] = idSet.size;
    }

    // Get this month's registrations
    const currentMonth = new Date();
    currentMonth.setDate(1);
    currentMonth.setHours(0, 0, 0, 0);

    const thisMonthQuery = await getDocs(
      query(usersRef, where("createdAt", ">=", currentMonth))
    );

    return {
      success: true,
      stats: {
        totalUsers: allUsersQuery.size,
        activeUsers: activeUsersQuery.size,
        blockedUsers: blockedUsersQuery.size,
        inactiveUsers: allUsersQuery.size - activeUsersQuery.size - blockedUsersQuery.size,
        thisMonthUsers: thisMonthQuery.size,
        roleStats: roleStats,
      },
    };
  } catch (error) {
    console.error("Error fetching user stats:", error);
    return {
      success: false,
      error: error.message,
      stats: {
        totalUsers: 0,
        activeUsers: 0,
        blockedUsers: 0,
        inactiveUsers: 0,
        thisMonthUsers: 0,
        roleStats: {},
      },
    };
  }
};

// Get user activity log
export const getUserActivityLog = async (userUid, limitCount = 50) => {
  try {
    const activitiesQuery = query(
      collection(db, "userActivities"),
      where("targetUserId", "==", userUid),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(activitiesQuery);
    const activities = [];

    querySnapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      activities,
    };
  } catch (error) {
    console.error("Error fetching user activity log:", error);
    return {
      success: false,
      error: error.message,
      activities: [],
    };
  }
};

// Search users
export const searchUsers = async (searchTerm, filters = {}) => {
  try {
    let baseRef = collection(db, "users");

    // Apply status filter if specified
    if (filters.status) {
      if (filters.status === 'active') {
        baseRef = query(baseRef, where("isActive", "==", true), where("isBlocked", "==", false));
      } else if (filters.status === 'blocked') {
        baseRef = query(baseRef, where("isBlocked", "==", true));
      } else if (filters.status === 'inactive') {
        baseRef = query(baseRef, where("isActive", "==", false));
      }
    }

    let users = [];

    // Role filter handling: need to support both legacy role and roles[] array
    if (filters.role && filters.role !== 'all') {
      const [legacySnap, multiSnap] = await Promise.all([
        getDocs(query(baseRef, where("role", "==", filters.role))),
        getDocs(query(baseRef, where("roles", "array-contains", filters.role))),
      ]);
      const usersMap = new Map();
      legacySnap.forEach((d) => usersMap.set(d.id, { uid: d.id, ...d.data() }));
      multiSnap.forEach((d) => usersMap.set(d.id, { uid: d.id, ...d.data() }));
      users = Array.from(usersMap.values()).sort((a, b) => {
        const aTs = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const bTs = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return bTs - aTs;
      });
    } else {
      const snap = await getDocs(query(baseRef, orderBy("createdAt", "desc")));
      snap.forEach((docSnap) => {
        users.push({ uid: docSnap.id, ...docSnap.data() });
      });
    }

    // Client-side filtering for search term (since Firestore doesn't support complex text search)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      users = users.filter(user => 
        user.profile?.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.entityName?.toLowerCase().includes(searchLower) ||
        user.hospitalName?.toLowerCase().includes(searchLower) ||
        user.profile?.phone?.includes(searchTerm)
      );
    }

    return {
      success: true,
      users,
    };
  } catch (error) {
    console.error("Error searching users:", error);
    return {
      success: false,
      error: error.message,
      users: [],
    };
  }
};

// Bulk update users
export const bulkUpdateUsers = async (updates) => {
  try {
    const batch = writeBatch(db);
    
    updates.forEach(({ userUid, updateData, updatedBy }) => {
      const userRef = doc(db, "users", userUid);
      batch.update(userRef, {
        ...updateData,
        updatedBy: updatedBy,
        updatedAt: new Date(),
      });
    });

    await batch.commit();

    // Log bulk activity
    await addDoc(collection(db, "userActivities"), {
      action: "bulk_user_update",
      performedBy: updates[0]?.updatedBy,
      timestamp: new Date(),
      details: {
        updatedCount: updates.length,
        actionType: "bulk_update"
      }
    });

    return {
      success: true,
      message: `${updates.length} users updated successfully`,
    };
  } catch (error) {
    console.error("Error in bulk update:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Update user profile
export const updateUserProfile = async (userUid, profileData, updatedBy) => {
  try {
    const userRef = doc(db, "users", userUid);
    
    await updateDoc(userRef, {
      profile: {
        ...profileData,
      },
      updatedBy: updatedBy,
      updatedAt: new Date(),
    });

    // Log the activity
    await addDoc(collection(db, "userActivities"), {
      action: "profile_updated",
      targetUserId: userUid,
      performedBy: updatedBy,
      timestamp: new Date(),
      details: {
        userUid: userUid,
        actionType: "profile_update"
      }
    });

    return {
      success: true,
      message: "Profile updated successfully",
    };
  } catch (error) {
    console.error("Error updating profile:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get recent user activities (for dashboard)
export const getRecentUserActivities = async (limitCount = 20) => {
  try {
    const activitiesQuery = query(
      collection(db, "userActivities"),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(activitiesQuery);
    const activities = [];

    querySnapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      activities,
    };
  } catch (error) {
    console.error("Error fetching recent activities:", error);
    return {
      success: false,
      error: error.message,
      activities: [],
    };
  }
};

// Delete user (soft delete by deactivating)
export const deleteUser = async (userUid, deletedBy, reason) => {
  try {
  // Use an admin Callable Cloud Function to delete Auth user and Firestore doc atomically
  const callDelete = httpsCallable(functions, "adminDeleteUser");
  const res = await callDelete({ userUid, reason, deletedBy });

  return { success: true, message: res?.data?.message || "User deleted successfully" };
  } catch (error) {
    console.error("Error deleting user:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Export user data
export const exportUserData = async (filters = {}) => {
  try {
    const result = await searchUsers("", filters);
    
    if (!result.success) {
      return result;
    }

    // Format data for export
    const exportData = result.users.map(user => ({
      uid: user.uid,
      name: user.profile?.name || user.entityName || user.hospitalName,
      email: user.email,
      role: user.role,
  roles: Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []),
      phone: user.profile?.phone,
      isActive: user.isActive,
      isBlocked: user.isBlocked,
      createdAt: user.createdAt?.toDate ? user.createdAt.toDate().toISOString() : user.createdAt,
      lastLogin: user.lastLogin?.toDate ? user.lastLogin.toDate().toISOString() : user.lastLogin,
    }));

    return {
      success: true,
      data: exportData,
      count: exportData.length,
    };
  } catch (error) {
    console.error("Error exporting user data:", error);
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
};

// Update user fields (role, roles, isActive, profile) in one call
export const updateUserFields = async (userUid, updateData, updatedBy) => {
  try {
    const userRef = doc(db, "users", userUid);

    const payload = {
      updatedBy,
      updatedAt: new Date(),
    };

    if (updateData.profile) {
      payload.profile = { ...updateData.profile };
    }
    if (typeof updateData.isActive === 'boolean') {
      payload.isActive = updateData.isActive;
    }
    if (Array.isArray(updateData.roles)) {
      payload.roles = updateData.roles;
    }
    if (updateData.role) {
      payload.role = updateData.role;
    }

    await updateDoc(userRef, payload);

    await addDoc(collection(db, "userActivities"), {
      action: "user_updated",
      targetUserId: userUid,
      performedBy: updatedBy,
      timestamp: new Date(),
      details: {
        fields: Object.keys(payload),
        actionType: "update_fields",
      },
    });

    return { success: true, message: "User updated successfully" };
  } catch (error) {
    console.error("Error updating user fields:", error);
    return { success: false, error: error.message };
  }
};